// src/components/nir/Edit.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Alert } from "react-bootstrap";
import { toast } from "react-toastify";
import { authFetch } from "../utils/authFetch";
import { NirForm } from "./Form";
import DocumentToolbar from "../common/DocumentToolbar";
import useDocumentClosing from "../../hooks/useDocumentClosing";
import { useAuth } from "../auth/AuthContext";
import ViewNotaModal from "../ViewNotaModal"; // <= default export, fara {}

export default function NirEdit() {
  const { id } = useParams();
  const { user } = useAuth();
  const closingEnabled = useDocumentClosing();
  const [initialForm, setInitialForm] = useState(null);
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showNota, setShowNota] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await authFetch(`/nir/${id}`);
        setForm(data);
        setInitialForm(data);
      } catch (e) { setError(String(e.message || e)); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const save = async () => {
    if (saving || form?.bxfinal_bool) return;
    setSaving(true); setError("");
    try {
      await authFetch(`/nir/${id}`, { method: "PUT", body: JSON.stringify(form) });
      toast.success("NIR-ul a fost salvat cu succes!");
      const data = await authFetch(`/nir/${id}`);
      setForm(data);
      setInitialForm(data);
    } catch (e) {
      setError(String(e.message || e));
      toast.error(`Eroare la salvare: ${e.message || e}`);
    }
    finally { setSaving(false); }
  };

  const finalize = async () => {
    if (!window.confirm("Finalizezi NIR-ul și intrarea asociată? După finalizare, documentele nu mai pot fi modificate sau șterse.")) {
      return;
    }
    setSaving(true); setError("");
    try {
      await authFetch(`/nir/${id}/finalize`, { method: "POST" });
      toast.success("NIR-ul a fost finalizat cu succes!");
      const data = await authFetch(`/nir/${id}`);
      setForm(data);
      setInitialForm(data);
    } catch (e) {
      setError(String(e.message || e));
      toast.error(`Eroare la finalizare: ${e.message || e}`);
    }
    finally { setSaving(false); }
  };

  const reopen = async () => {
    if (!window.confirm("Redeschizi NIR-ul și intrarea asociată pentru modificări?")) return;
    setSaving(true);
    setError("");
    try {
      await authFetch(`/nir/${id}/unfinalize`, { method: "POST" });
      const data = await authFetch(`/nir/${id}`);
      setForm(data);
      setInitialForm(data);
      toast.success("NIR redeschis.");
    } catch (e) {
      setError(e.message || "Nu am putut redeschide NIR-ul.");
    } finally { setSaving(false); }
  };

  if (loading) return <Container className="py-3">Se incarca…</Container>;
  if (!form) return <Container className="py-3 text-danger">Nu s-a gasit inregistrarea.</Container>;

  const isFinal = !!form?.bxfinal_bool;
  const isDirty = JSON.stringify(form) !== JSON.stringify(initialForm);
  const sourceId = form.intrare_id || form.source_intrare_id;

  return (
    <Container className="py-3">
      <DocumentToolbar
        title={`NIR #${id}`} isFinal={isFinal} busy={saving} closingEnabled={closingEnabled}
        onBack={() => navigate(sourceId ? `/intrare/${sourceId}/edit` : "/nir")}
        backLabel={sourceId ? "Înapoi la intrare" : "Înapoi"}
        onSave={save} saveDisabled={!isDirty} onFinalize={finalize} finalizeDisabled={isDirty}
        canReopen={user?.is_superuser || user?.user_name === "admin" || user?.perms?.includes("INTRARE_VALIDATE")}
        onReopen={reopen}
        actions={[
          { label: "Tipărește", onClick: () => window.open(`/nir/${id}/print`, "_blank") },
          { label: "Vezi nota contabilă", onClick: () => setShowNota(true) },
        ]}
      />

      {error && <Alert variant="danger">{error}</Alert>}

      <NirForm value={form} onChange={setForm} onSubmit={save} submitting={saving} showActions={false} />

      <ViewNotaModal
        recType={1}                // 2 = NIR (1 = Intrari)
        docId={Number(id)}         // converteste id-ul din URL la numar
        show={showNota}
        onHide={() => setShowNota(false)}
        // seteaza la true daca ai endpoint de tip /api/nota/nir/7
        // usePath={true}
      />
    </Container>
  );
}
